'use client';

import {
  BusinessShoppingCart,
  BusinessShoppingCartSolid,
  NavigationCross,
} from '@energy-retailer/icons';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import { Dialog, Transition } from '@headlessui/react';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment } from 'react';

import { Button } from '@/components/button';
import { Text } from '@/components/text';
import { useStore } from '@/lib/use-store';

import type { ReactNode } from 'react';

export const Drawer = ({
  children,
  isOpen,
  setOpen,
}: {
  children: ReactNode;
  isOpen: boolean;
  setOpen: (arg: boolean) => void;
}) => {
  const cartStore = useStore(useJoinFormStore, (state) => state);
  const router = useRouter();
  const pathname = usePathname();

  const proceedToSignUpPage = () => {
    setOpen(false);
    router.push('/sign-up/details');
  };
  return (
    <Transition.Root
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        as="div"
        className="relative z-40"
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black opacity-[0.16]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex md:w-2/4 xl:max-w-md">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen">
                  <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                    <div className="flex min-h-0 flex-1 flex-col divide-y divide-gray-200 overflow-y-scroll">
                      <div className="flex items-center justify-between">
                        <Dialog.Title
                          as="div"
                          className="flex items-center pl-e md:pl-d"
                        >
                          <div className="relative mr-b h-icon w-icon lg:mr-a">
                            {cartStore?.electricityPlanId ||
                              cartStore?.gasPlanId ? (
                              <div className="relative h-6 w-6">
                                <div className="absolute right-0 top-0 h-2 w-2 rounded-full bg-pink-500" />
                                <BusinessShoppingCartSolid />
                              </div>
                            ) : (
                              <BusinessShoppingCart />
                            )}
                          </div>
                          <Text
                            as="h3"
                            variant="h3"
                          >
                            Your cart
                          </Text>
                        </Dialog.Title>
                        <div className="flex items-center">
                          <button
                            type="button"
                            className="relative px-e py-d md:px-d md:py-c xl:py-b"
                            onClick={() => {
                              setOpen(false);
                            }}
                          >
                            <span className="sr-only">Close panel</span>
                            <NavigationCross className="h-icon w-icon" />
                          </button>
                        </div>
                      </div>
                      <div className="relative flex-1 p-e md:px-d md:pt-c">
                        {children}
                      </div>
                    </div>
                    {!pathname.includes('/sign-up') ? (
                      <div className="flex shrink-0 px-e pb-e pt-c md:px-d md:pb-d md:pt-b xl:pb-b">
                        <Button
                          variant="primary"
                          size="default"
                          className="w-full"
                          disabled={!cartStore?.fluxId && !cartStore?.gasFluxId}
                          onClick={proceedToSignUpPage}
                        >
                          Sign up
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
